import type { Key } from '@/i18n';
import { FLIGHT_STATUSES } from '@/types/flight';
import type { FieldChange } from './api';

/**
 * How a field change reads.
 *
 * Pure — no React, no styles — because this is the part with judgement in it, and
 * judgement is worth being able to run. `ChangeList` in `parts.tsx` renders what
 * these decide.
 *
 * Three things the payload does that a screen must not repeat:
 *
 * - **Field names arrive in camelCase** — `actualDate`, `planeParkingName` — because
 *   they are the API's own column names. They are mapped to labels here, and an
 *   unmapped one falls back to the raw name: a field added server-side should look
 *   unfamiliar, not disappear.
 * - **An absent value arrives as the STRING `"null"`**, not as JSON null. So the
 *   obvious `value ?? '—'` does not catch it and the word "null" reaches the reader —
 *   which is exactly what it used to do.
 * - **A status arrives as its enum name** (`DELAYED`), which the board already has a
 *   translation for.
 *
 * Direction is shown by presence, not punctuation: a field that HAD no value shows
 * only what it became, because `— → 09:41` spends a glyph saying "previously
 * nothing" on every line of a creation, where every line is that.
 */

export type Translate = (key: Key, values?: Record<string, string | number>) => string;

/**
 * The changeable columns, as `AirportScheduleInfo` names them — the closed list the
 * API documents for `changes[].field` and the `changedField` filter.
 */
const FIELD_LABELS: Record<string, Key> = {
  scheduledDate: 'monitor.fields.scheduledDate',
  estimatedDate: 'monitor.fields.estimatedDate',
  actualDate: 'monitor.fields.actualDate',
  departedTime: 'monitor.fields.departedTime',
  flightStatus: 'monitor.fields.flightStatus',
  delayMinutes: 'monitor.fields.delayMinutes',
  delayReason: 'monitor.fields.delayReason',
  passengerAmount: 'monitor.fields.passengerAmount',
  totalCargoWeight: 'monitor.fields.totalCargoWeight',
  planeParkingName: 'monitor.fields.planeParkingName',
  externalFlightId: 'monitor.fields.externalFlightId',
  active: 'monitor.fields.active',
};

export const fieldLabel = (field: string, t: Translate): string => {
  const key = FIELD_LABELS[field];
  return key ? t(key) : field;
};

/**
 * Whether a value means "there was nothing here".
 *
 * `"null"` is the one that matters: the API stringifies every value, so an absent
 * one is the four characters n-u-l-l rather than JSON null.
 */
export const isEmpty = (value: string | null): boolean => {
  if (value === null) return true;

  const trimmed = value.trim();
  return trimmed === '' || trimmed === 'null';
};

const isFlightStatus = (value: string): boolean =>
  (FLIGHT_STATUSES as readonly string[]).includes(value);

/** A value in the reader's language where the field has one, verbatim otherwise. */
export const formatValue = (field: string, value: string, t: Translate): string => {
  const trimmed = value.trim();

  // The board translates these already; showing `DELAYED` beside its own table that
  // says "Delayed" would be two vocabularies for one thing.
  if (field === 'flightStatus' && isFlightStatus(trimmed)) {
    return t(`schedule.statuses.${trimmed}` as Key);
  }

  if (field === 'active') {
    if (trimmed === 'true') return t('common.yes');
    if (trimmed === 'false') return t('common.no');
  }

  // Times arrive pre-formatted as `13.07.2026 21:15`, which is already this app's own
  // layout — parsing and reprinting them would only risk changing them.
  return trimmed;
};

/** One change as text: `Actual time: 21:15 → 21:22`, or `Passengers: 150` when set. */
export const describe = (change: FieldChange, t: Translate): string => {
  const label = fieldLabel(change.field, t);
  const to = isEmpty(change.newValue)
    ? t('monitor.changes.cleared')
    : formatValue(change.field, change.newValue ?? '', t);

  if (isEmpty(change.oldValue)) return `${label}: ${to}`;

  return `${label}: ${formatValue(change.field, change.oldValue ?? '', t)} → ${to}`;
};

/**
 * Every change on one line, for the table cell.
 *
 * A string rather than elements, because the cell truncates with an ellipsis and
 * that only works on text. The full list is one click away in the expanded row.
 */
export const summarise = (changes: readonly FieldChange[], t: Translate): string =>
  changes.map((change) => describe(change, t)).join(' · ');
